import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keepPreviousData } from '@tanstack/react-query'
import {
  createAdminWorkflow,
  getAdminOrganizationWorkflows,
  getAdminWorkflows,
  updateAdminWorkflow,
  type AdminWorkflowListParams,
  type CreateAdminWorkflowPayload,
  type UpdateAdminWorkflowPayload,
} from '@/services/api'

const workflowsQueryKey = (organizationId: string, audience: string) =>
  ['admin-organization-workflows', organizationId, audience] as const

export function useOrganizationWorkflows(
  organizationId: string | undefined,
  audience: 'customer' | 'test' | 'all' = 'all',
) {
  return useQuery({
    queryKey: workflowsQueryKey(organizationId ?? '', audience),
    queryFn: () => getAdminOrganizationWorkflows(organizationId!, audience),
    enabled: !!organizationId,
  })
}

export function useCreateWorkflow(organizationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateAdminWorkflowPayload) => createAdminWorkflow(organizationId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-organization-workflows', organizationId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-workflow-list'] })
    },
  })
}

export function useUpdateWorkflow(organizationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workflowId, ...body }: { workflowId: string } & UpdateAdminWorkflowPayload) =>
      updateAdminWorkflow(organizationId, workflowId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-organization-workflows', organizationId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-workflow-list'] })
    },
  })
}

export function useWorkflowList(params: AdminWorkflowListParams) {
  return useQuery({
    queryKey: ['admin-workflow-list', params] as const,
    queryFn: () => getAdminWorkflows(params),
    placeholderData: keepPreviousData,
  })
}
